import { useCallback } from 'react';
import { useShowToast } from '@/hooks';
import {
  usePreviewAdhocEsignDocumentsMutation,
  usePreviewEsignDocumentsMutation,
} from '@/store/query/esign-documents';
import type { EsignSendOverrides } from '@/store/query/esign-documents/esignSendOverrides';
import {
  useEsignPreviewFlow,
  esignPreviewErrorToast,
} from '@/components/shared/esignature/useEsignPreviewFlow';
import { ESIGN_ACTIVITY_COPY } from '@/constants/esignature';
import type { EsignActivityPayload } from '@/types/esignature';
import { esignActivityErrorCopy } from './esignActivityErrors';

interface PreviewArgs {
  atsJobStageActionId: string | number;
  atsApplicationId: string | number;
  overrides?: EsignSendOverrides;
}

interface PreviewAdhocArgs {
  atsApplicationId: string | number;
  atsJobStageId: string | number;
  params: EsignActivityPayload;
}

/**
 * "Preview on Docusign" for a configured e-sign activity: enqueue the draft,
 * poll until DocuSign has it, then hand the recruiter the sender view.
 *
 * The enqueue is separated from the poll because only the enqueue can fail with
 * a config error worth naming (`err_docusign_not_enabled`, a stale activity
 * id); once it returns 202 every remaining failure is an envelope failure and
 * carries its own message.
 */
export const usePreviewEsignDocuments = () => {
  const toast = useShowToast();
  const [previewEsignDocuments, { isLoading: isEnqueuing }] =
    usePreviewEsignDocumentsMutation();
  const [previewAdhocEsignDocuments, { isLoading: isEnqueuingAdhoc }] =
    usePreviewAdhocEsignDocumentsMutation();
  const { runPreview: pollAndRedirect, isRunning } = useEsignPreviewFlow();

  /** Shared tail: name a config error, then poll and hand over to DocuSign. */
  const settle = useCallback(
    async (enqueued: any) => {
      if ('error' in enqueued) {
        toast({
          title: esignActivityErrorCopy(enqueued.error),
          status: 'error',
        });
        return;
      }

      const envelopeId = enqueued.data?.id;
      if (!envelopeId) {
        toast({ title: ESIGN_ACTIVITY_COPY.PREVIEW_ERROR, status: 'error' });
        return;
      }

      try {
        await pollAndRedirect({ id: envelopeId });
      } catch (error) {
        toast(esignPreviewErrorToast(error, ESIGN_ACTIVITY_COPY.PREVIEW_ERROR));
      }
    },
    [pollAndRedirect, toast]
  );

  /**
   * Preview the envelope for one candidate.
   *
   * `overrides` carries whatever the recruiter changed in the send drawer and
   * nothing else. It stays a `/preview` on the stage action, so the envelope
   * remains the planned activity — same row, same grouping, same status
   * machine — while the stage's template, and every other candidate on it, is
   * left alone. Pass no overrides and this is the untouched send path.
   */
  const runPreview = useCallback(
    async ({
      atsJobStageActionId,
      atsApplicationId,
      overrides,
    }: PreviewArgs) => {
      const enqueued = await previewEsignDocuments({
        atsJobStageActionId,
        atsApplicationId,
        overrides,
      });

      await settle(enqueued);
    },
    [previewEsignDocuments, settle]
  );

  /**
   * Preview a one-off envelope on a stage with NO configured e-sign activity.
   *
   * This is the only correct use of `/preview_adhoc`. It takes the whole payload
   * because there is no stage action to read a configuration from, and the
   * result is grouped under `stage.adhoc_esign_documents[]` rather than becoming
   * a planned activity — which is exactly why it must NOT be used to tailor a
   * configured activity for one candidate. That case is `runPreview` with
   * `overrides`: it keeps the envelope attached to its stage action, with the
   * activity's row, grouping and status machine intact.
   *
   * `ats_job_stage_id` is required, not optional as the original handoff had it:
   * with no stage action to hang off, the backend cannot otherwise group it.
   */
  const runAdhocPreview = useCallback(
    async ({ atsApplicationId, atsJobStageId, params }: PreviewAdhocArgs) => {
      const enqueued = await previewAdhocEsignDocuments({
        atsApplicationId,
        atsJobStageId,
        params,
      });
      await settle(enqueued);
    },
    [previewAdhocEsignDocuments, settle]
  );

  return {
    runPreview,
    runAdhocPreview,
    isPreviewing: isEnqueuing || isEnqueuingAdhoc || isRunning,
  };
};
